<template>
  <a
    v-if="isExternal"
    :href="computedUrl"
    class="Link"
    target="_blank"
    rel="noopener noreferrer"
  >
    <slot />
  </a>
  <router-link
    v-else
    :to="computedUrl"
    class="Link"
    :class="{ 'is-active': isActive }"
  >
    <slot />
  </router-link>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

defineOptions({ name: 'Link' })

const props = defineProps({
  to: {
    type: [String, Object],
    required: true
  },
  anchorID: {
    type: String,
    default: null
  }
})

const route = useRoute()

// Compute the final URL from either string or link object, with optional anchorID prop
const computedUrl = computed(() => {
  let baseUrl = '/'
  let anchorId = props.anchorID // Use prop anchorID if provided
  
  // Handle different input types
  if (typeof props.to === 'string') {
    const trimmedTo = props.to.trim()
    baseUrl = trimmedTo || '/'
  } else if (props.to && typeof props.to === 'object') {
    // If it's a link object, build URL from slug and use its anchorID if no prop provided
    if (props.to.slug && typeof props.to.slug === 'string') {
      const trimmedSlug = props.to.slug.trim()
      if (trimmedSlug && trimmedSlug !== 'undefined' && trimmedSlug !== 'null') {
        baseUrl = trimmedSlug.startsWith('/') ? trimmedSlug : `/${trimmedSlug}`
      }
    }
    if (!anchorId && props.to.anchorID && typeof props.to.anchorID === 'string') {
      const trimmedAnchor = props.to.anchorID.trim()
      if (trimmedAnchor && trimmedAnchor !== 'undefined' && trimmedAnchor !== 'null') {
        anchorId = trimmedAnchor
      }
    }
  }
  
  // Append anchor if present and valid
  if (anchorId && typeof anchorId === 'string') {
    const trimmedAnchor = anchorId.trim()
    if (trimmedAnchor && trimmedAnchor !== 'undefined' && trimmedAnchor !== 'null') {
      return `${baseUrl}#${trimmedAnchor}`
    }
  }
  
  return baseUrl
})

// anything beginning with http(s), mailto or tel is external
const isExternal = computed(() =>
  /^(https?:|mailto:|tel:)/.test(computedUrl.value)
)

// Check if the current route matches the link URL
const isActive = computed(() => {
  if (isExternal.value) return false
  
  const currentPath = route.path
  const linkPath = computedUrl.value.split('#')[0] // Remove anchor for comparison
  
  // Exact match or if current path starts with link path (for nested routes)
  return currentPath === linkPath || currentPath.startsWith(linkPath + '/')
})
</script>

<style lang="scss" scoped>
@use '../assets/scss/variables' as *;
@use '../assets/scss/typography' as *;

.Link {
  @extend %p;
  display: inline-block;
  padding: 0;
  color: var(--theme-link);
  background: transparent;
  text-decoration: none;
  border-bottom: 1px solid var(--theme-link);
  transition:
    color 0.3s ease,
    border-bottom-color 0.3s ease,
    transform 0.3s ease;

  &:hover {
    color: var(--theme-link-hover);
    border-bottom-color: var(--theme-link-hover);
  }

  &:active {
    color: var(--theme-link-active);
    border-bottom-color: var(--theme-link-active);
    transform: translateY(-2px);
  }

  &.is-active {
    color: var(--theme-link-active);
    border-bottom-color: var(--theme-link-active);
    font-weight: 600;
  }
}
</style>